import React, { useState, useEffect } from "react";

// Define the colors for different states
const COLORS = {
  CORRECT: "bg-green-500",
  PRESENT: "bg-yellow-500",
  ABSENT: "bg-gray-500",
  EMPTY: "bg-white",
};

const THEMES = {
  "Pie Day": "PIE IN THE SKY",
  "Secondhand Sunday": "IT'S BETTER FOR THE PLANET",
  "Eat a Red Apple Day": "AN APPLE A DAY KEEPS THE DR AWAY",
  "Rosa Parks Day": "TAKE A SEAT FOR YOUR RIGHTS",
  "Antarctica Day": "THE COOLEST PLACE ON EARTH",
};

type ThemeKey = keyof typeof THEMES;

const PhraseWordle: React.FC = () => {
  const [currentTheme, setCurrentTheme] = useState<ThemeKey>("Pie Day");
  const [phrase, setPhrase] = useState(THEMES[currentTheme]);
  const [guesses, setGuesses] = useState<string[]>([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  // Create an array of words based on the phrase
  const phraseWords = phrase.split(" ");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.toUpperCase();
    // Only allow letters and spaces, and limit to the length of the phrase
    const sanitizedInput = input
      .replace(/[^A-Z ]/g, "")
      .slice(0, phrase.length);
    setCurrentGuess(sanitizedInput);
  };

  const handleSubmitGuess = () => {
    if (
      currentGuess.replace(/ /g, "").length !== phrase.replace(/ /g, "").length
    )
      return;

    const newGuesses = [...guesses, currentGuess];
    setGuesses(newGuesses);
    setCurrentGuess("");

    if (currentGuess === phrase) {
      setIsCorrect(true);
      setGameOver(true);
      // Trigger animation by updating state
      setGuesses([...newGuesses]);
    } else if (newGuesses.length === 6) {
      setGameOver(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmitGuess();
    }
  };

  const handleThemeChange = (newTheme: ThemeKey) => {
    setCurrentTheme(newTheme);
    setPhrase(THEMES[newTheme]);
    setGuesses([]);
    setCurrentGuess("");
    setGameOver(false);
    setIsCorrect(false);
  };

  const getLetterColor = (
    guessWord: string,
    phraseWord: string,
    letterIndex: number
  ): string => {
    if (guessWord[letterIndex] === phraseWord[letterIndex]) {
      return COLORS.CORRECT;
    } else if (phraseWord.includes(guessWord[letterIndex])) {
      return COLORS.PRESENT;
    } else {
      return COLORS.ABSENT;
    }
  };

  const renderGrid = () => {
    const rows = [];
    for (let i = 0; i < 6; i++) {
      const row = [];
      let charIndex = 0;
      const guessWords = guesses[i]?.split(" ") || [];

      for (let wordIndex = 0; wordIndex < phraseWords.length; wordIndex++) {
        const phraseWord = phraseWords[wordIndex];
        const guessWord = guessWords[wordIndex] || "";

        for (let j = 0; j < phraseWord.length; j++) {
          const guessChar = guessWord[j] || "";
          let color = COLORS.EMPTY;

          if (guesses[i]) {
            color = getLetterColor(guessWord, phraseWord, j);
          }

          row.push(
            <div
              key={`${i}-${charIndex}`}
              className={`w-6 h-6 sm:w-10 sm:h-10 border border-gray-300 flex items-center justify-center text-sm sm:text-xl font-bold ${color} ${isCorrect && i === guesses.length - 1 ? "scale-up" : "scale-normal"}`}
            >
              {guessChar}
            </div>
          );
          charIndex++;
        }

        if (wordIndex < phraseWords.length - 1) {
          row.push(
            <div key={`space-${i}-${charIndex}`} className="w-1 sm:w-2"></div>
          );
          charIndex++;
        }
      }
      rows.push(
        <div key={i} className="flex mb-1 sm:mb-2">
          {row}
        </div>
      );
    }
    return rows;
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
      <h1 className="text-2xl sm:text-4xl font-bold mb-4 sm:mb-8">Worday</h1>
      <h2 className="text-1xl sm:text-2xl font-bold mb-3 sm:mb-6 text-blue-600">
        Word Phrase for {currentTheme}
      </h2>
      <div className="mb-4">
        <label htmlFor="theme-select" className="sr-only">Select Theme</label>
        <select
          id="theme-select"
          onChange={(e) => handleThemeChange(e.target.value as ThemeKey)}
          value={currentTheme}
        >
            {Object.keys(THEMES).map((theme) => (
              <option key={theme} value={theme}>
                {theme}
              </option>
            ))}
        </select>
      </div>
      <div className="mb-4">{renderGrid()}</div>
      <div className="flex flex-col sm:flex-row gap-2 w-full max-w-xs sm:max-w-md">
        <input
          type="text"
          value={currentGuess}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          maxLength={phrase.length}
          disabled={gameOver}
          className="w-full"
          placeholder="Enter your guess"
        />
        <button
          type="button"
          onClick={handleSubmitGuess}
          disabled={
            gameOver ||
            currentGuess.replace(/ /g, "").length !==
              phrase.replace(/ /g, "").length
          }
          className="w-full sm:w-auto"
        >
          Guess
        </button>
      </div>
      {isCorrect && (
        <div className="mt-4 text-lg sm:text-xl font-bold text-center text-green-600 congratulations">
          Congratulations! You guessed the phrase correctly!
        </div>
      )}
      {gameOver && !isCorrect && (
        <div className="game-over-text">
          Game Over! The phrase was: {phrase}
        </div>
      )}
    </div>
  );
};

export default PhraseWordle;
